import React, { useState, useEffect } from "react";
import {
  Clock,
  Loader,
  BookOpen,
  User,
  ShieldAlert,
  Eye,
  ExternalLink,
  Receipt,
  FileText,
} from "lucide-react";
import { lmsGetStudentCourseRequests } from "../api/lmsApi";
import "../lms.css";

const getStudentData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const studentId = ud.student_id || ud.id || null;
    const schoolId = ud.school_id || ud.admin_id || ud.parent_id || null;
    return { studentId, schoolId, ...ud };
  } catch {
    return { studentId: null, schoolId: null };
  }
};

export default function StudentRequestedCourses() {
  const { studentId, schoolId } = getStudentData();

  const [courseRequests, setCourseRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    if (studentId) {
      fetchCourseRequests();
    } else {
      setLoading(false);
    }
  }, [studentId, schoolId]);

  const fetchCourseRequests = async () => {
    setLoading(true);
    try {
      const res = await lmsGetStudentCourseRequests(studentId, schoolId);
      const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setCourseRequests(data);
    } catch (err) {
      console.error("Error fetching requested courses for student:", err);
      setCourseRequests([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Loader size={32} className="spinner" style={{ margin: "0 auto 12px" }} />
        <p style={{ color: "#64748b", fontSize: "15px" }}>Loading your requested courses...</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Informational Header Notice */}
      <div
        style={{
          background: "#fffbe6",
          border: "1px solid #ffe58f",
          borderRadius: "12px",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <div
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "10px",
            background: "#fef3c7",
            color: "#d97706",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Clock size={22} />
        </div>
        <div>
          <h4 style={{ fontSize: "15px", fontWeight: "700", color: "#92400e", margin: 0 }}>
            Pending Course Enrollment Requests
          </h4>
          <p style={{ fontSize: "13px", color: "#78350f", margin: "2px 0 0", lineHeight: "1.4" }}>
            The courses below have been requested with payment details submitted and are awaiting approval from your school administration.
          </p>
        </div>
      </div>

      {/* Requested Courses Grid */}
      {courseRequests.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "50px 20px",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <Clock size={44} color="#94a3b8" style={{ margin: "0 auto 12px" }} />
          <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#334155" }}>
            No Pending Requested Courses
          </h4>
          <p style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
            You do not have any pending course enrollment requests at this moment.
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "24px",
          }}
        >
          {courseRequests.map((req, idx) => {
            const reqId = req.id || req.enrollment_id;
            const origPrice = parseFloat(req.course_price) || 0;
            const discPrice = parseFloat(req.course_discount_price) || origPrice;
            const targetFee = discPrice || origPrice;
            const paidAmt = parseFloat(req.requested_amount || req.total_paid_amount || 0);

            return (
              <div
                key={reqId || idx}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header Banner */}
                <div
                  style={{
                    padding: "14px 18px",
                    background: "#fef9c3",
                    borderBottom: "1px solid #fef08a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span
                    style={{
                      background: "#fef3c7",
                      color: "#b45309",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "700",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Clock size={13} /> Pending Verification
                  </span>

                  <span style={{ fontSize: "12px", color: "#854d0e" }}>
                    Requested: {req.enrolled_at || req.created_at ? new Date(req.enrolled_at || req.created_at).toLocaleDateString("en-IN") : "Recent"}
                  </span>
                </div>

                {/* Body */}
                <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", gap: "12px", marginBottom: "14px" }}>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "8px",
                        background: "#1c498d",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        fontWeight: "700",
                        flexShrink: 0,
                      }}
                    >
                      {(req.course_name || "C").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#0f172a", margin: 0, lineHeight: "1.3" }}>
                        {req.course_name || `Course #${req.course_id}`}
                      </h3>
                      {req.instructor_name && (
                        <p style={{ fontSize: "12px", color: "#64748b", margin: "3px 0 0" }}>
                          Instructor: {req.instructor_name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Submission Summary */}
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "8px",
                      padding: "12px 14px",
                      marginBottom: "16px",
                      fontSize: "13px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                      <span style={{ color: "#64748b" }}>Course Fee:</span>
                      <strong style={{ color: "#334155" }}>₹{targetFee.toLocaleString("en-IN")}</strong>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                      <span style={{ color: "#64748b" }}>Submitted Amount:</span>
                      <strong style={{ color: "#16a34a", fontSize: "14px" }}>₹{paidAmt.toLocaleString("en-IN")}</strong>
                    </div>

                    {req.transaction_id && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: req.payment_screenshot ? "6px" : "0" }}>
                        <span style={{ color: "#64748b" }}>Txn / UTR ID:</span>
                        <span style={{ fontFamily: "monospace", color: "#1c498d", fontWeight: "700" }}>
                          {req.transaction_id}
                        </span>
                      </div>
                    )}

                    {req.payment_screenshot && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "4px" }}>
                        <span style={{ color: "#64748b" }}>Receipt Proof:</span>
                        <button
                          type="button"
                          onClick={() => setPreviewImage(req.payment_screenshot)}
                          style={{
                            color: "#1c498d",
                            fontWeight: "600",
                            fontSize: "12px",
                            border: "none",
                            background: "none",
                            textDecoration: "underline",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Eye size={13} /> View Image
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Status Banner */}
                  <div
                    style={{
                      marginTop: "auto",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      fontSize: "12px",
                      color: "#92400e",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <ShieldAlert size={16} color="#d97706" />
                    <span>Awaiting admin approval to unlock lessons.</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Screenshot Preview Modal */}
      {previewImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(15, 23, 42, 0.75)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "520px",
              width: "100%",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "16px 20px",
                background: "#1c498d",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 style={{ fontSize: "16px", fontWeight: "700", margin: 0 }}>
                Submitted Payment Screenshot
              </h3>
              <button
                type="button"
                onClick={() => setPreviewImage(null)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ padding: "20px", textAlign: "center", background: "#f8fafc" }}>
              <img
                src={previewImage}
                alt="Payment Receipt Screenshot"
                style={{
                  maxWidth: "100%",
                  maxHeight: "420px",
                  borderRadius: "8px",
                  objectFit: "contain",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              />
              <div style={{ marginTop: "14px" }}>
                <a
                  href={previewImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#1c498d",
                    fontWeight: "600",
                    fontSize: "13px",
                    textDecoration: "underline",
                  }}
                >
                  <ExternalLink size={14} /> Open Image in New Tab
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
